import confetti from 'canvas-confetti';
import { ToastOptions } from 'react-toastify';

import { PetActivity, PetBody } from '@shared/types/types';

// Every consumer resolves a country name by finding the matching code here, and
// falls back to an empty string when the lookup misses. CH was absent, so a Swiss
// customer's shipping and billing addresses rendered with no country line at all.
// Keep this in step with petcon's COUNTRIES in constants/countries.ts.
export const COUNTRIES = [
  { code: 'AT', translation: 'GLOBAL.COUNTRY.AT' },
  { code: 'DE', translation: 'GLOBAL.COUNTRY.DE' },
  { code: 'CH', translation: 'GLOBAL.COUNTRY.CH' },
];

export const REDIRECT_URL = 'https://www.hellobello.at/a/loop_subscriptions/get-subscription-link';

export const NEW_SUITE_TAG = 'New';

export const isNewSuiteProduct = (product?: IProduct | null): boolean =>
  Array.isArray(product?.tags) &&
  product.tags.some((tag) => tag?.trim().toLowerCase() === NEW_SUITE_TAG.toLowerCase());

export const FRESH_PRODUCTS = [
  { code: 'CG', translation: 'ECOMMERCE.PRODUCTS.FRESH.CG.TITLE' },
  { code: 'GS', translation: 'ECOMMERCE.PRODUCTS.FRESH.GS.TITLE' },
  { code: 'RT', translation: 'ECOMMERCE.PRODUCTS.FRESH.RT.TITLE' },
  { code: 'WS', translation: 'ECOMMERCE.PRODUCTS.FRESH.WS.TITLE' },
];

export const TOAST_CONFIG: ToastOptions = {
  position: 'top-center',
  autoClose: 20000,
  hideProgressBar: false,
  closeOnClick: true,
  rtl: false,
  pauseOnFocusLoss: true,
  draggable: false,
  pauseOnHover: true,
};

export const DEFAULT_TIMEZONE = 'Europe/Berlin';

// `translation` holds a message id, matching COUNTRIES and FRESH_PRODUCTS above.
// These reuse the same PET.ACTIVITY.* / PET.BODY.* ids the pet-profile-update
// flow uses, so an activity level reads identically in both places.
export const PET_ACTIVITIES = [
  {
    key: 'calm',
    value: PetActivity.calm,
    translation: 'PET.ACTIVITY.CALM',
    descriptionTranslation: 'PET.ACTIVITY.CALM.DESC',
  },
  {
    key: 'normal',
    value: PetActivity.normal,
    translation: 'PET.ACTIVITY.NORMAL',
    descriptionTranslation: 'PET.ACTIVITY.NORMAL.DESC',
  },
  {
    key: 'active',
    value: PetActivity.active,
    translation: 'PET.ACTIVITY.ACTIVE',
    descriptionTranslation: 'PET.ACTIVITY.ACTIVE.DESC',
  },
];

export const PET_BODIES = [
  {
    key: 'skinny',
    value: PetBody.skinny,
    translation: 'PET.BODY.SKINNY',
  },
  {
    key: 'ideal',
    value: PetBody.ideal,
    translation: 'PET.BODY.IDEAL',
  },
  {
    key: 'chunky',
    value: PetBody.chunky,
    translation: 'PET.BODY.CHUNKY',
  },
];

export const sachetTypeConverted: { [key: string]: string } = {
  CG: 'Cowgenuss',
  GS: 'Gockelspeis',
  RT: 'Ringeltanz',
  WS: 'Wildschmaus', // TODO(WS): confirm brand name.
};

export const sachetIcons: Record<string, string> = {
  cg: '/media/img/subscription/cowgenuss-new.webp',
  rt: '/media/img/subscription/ringeltanz-new.webp',
  gs: '/media/img/subscription/gockelspeis-new.webp',
  ws: '/media/img/subscription/ringeltanz-new.webp', // TODO(WS): real WS icon image.
};

export const skuLeckerliesRegex = /\d{3}-\d{4}-L-(RH|BR|WH|HH|GB|CT|LOE|SM|BA|ZB|LT)(-SUP)?/;

// Fresh food SKU pattern: e.g. "0726-CGGSRT-V-28", "0500-CG-H-14", "0500-CGGS-V-TEST"
// Format: {4-digit kcal}-{recipe codes}-{H|V pension}-{cycle in days, or TEST}, e.g. 0500-CGGSRT-V-35.
export const skuFreshRegex = /^\d{4}-[A-Z]+-[HV]-(\d{1,3}|TEST)$/i;

export const rewardConfetti = (x: number, y: number, offsetX: number) => {
  const isMobile = window.innerWidth <= 768;

  const particleCount = isMobile ? 100 : 200;
  const spread = isMobile ? 20 : 40;

  // The returned promise only signals that the animation finished; nothing waits for it.
  void confetti({
    particleCount,
    spread,
    origin: { x: x + offsetX, y },
  });
};

// Recipe key -> MEAT.* message id, so a recipe's meat reads the same wherever it
// appears. Holds ids, not display text.
export const recipeMeatIds: Record<string, string> = {
  cg: 'MEAT.BEEF',
  gs: 'MEAT.CHICKEN',
  rt: 'MEAT.PORK',
  ws: 'MEAT.GAME',
};

export const recipeIconSlugs: Record<string, string> = {
  cg: 'rind',
  gs: 'huhn',
  rt: 'schwein',
  ws: 'wild', // TODO(WS): needs /media/svg/general/wild-icon.svg (or confirm slug).
};

// Pack shots for the fresh food line, keyed by the recipe combination in the
// canonical CG -> GS -> RT -> WS order (the same order the fresh SKU uses, e.g.
// "0726-CGGSRT-V-28"). All 15 non-empty combinations of the four recipes are
// covered; keep this in sync with the Shopify theme's `image_url` case.
export const freshRecipeImages: Record<string, string> = {
  CG: 'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Cowgenuss_6.webp?v=1782896926',
  GS: 'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Gockelspeis_6.webp?v=1782896925',
  RT: 'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Ringeltanz_6.webp?v=1782896925',
  WS: 'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Wildschmaus_1.webp?v=1786544301',
  CGGS: 'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Cowgenuss_Gockelspeis_2c8d183b-e7ec-4eab-a11f-ffa0c4e24597.webp?v=1782897000',
  CGRT: 'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Cowgenuss_Ringeltanz_5bb4d32e-2a31-456e-bcb9-ef8e97503a0f.webp?v=1782896925',
  CGWS: 'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Cowgenuss_Wildschmaus_1.webp?v=1786544302',
  GSRT: 'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Gockelspeis_Ringeltanz_506879f6-6f6d-47cf-bb89-60bebe5524da.webp?v=1782896925',
  GSWS: 'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Gockelspeis_Wildschmaus.webp?v=1786544302',
  RTWS: 'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Ringeltanz_Wildschmaus_1.webp?v=1786544301',
  CGGSRT:
    'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/All_3_recipes_1.webp?v=1782896925',
  CGGSWS:
    'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Cowgenuss_Gockelspeis_Wildschmaus.webp?v=1786544495',
  CGRTWS:
    'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Cowgenuss_Ringeltanz_Wildschmaus_2.webp?v=1786544301',
  // Note the source filename really is "Gockelspeic" (typo on the CDN side).
  GSRTWS:
    'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Gockelspeic_Ringeltanz_Wildschmaus_1.webp?v=1786544302',
  CGGSRTWS:
    'https://cdn.shopify.com/s/files/1/0410/1403/4599/files/Cowgenuss_Gockelspeis_Ringeltanz_Wildschmaus.webp?v=1786544301',
};

// Canonical ordering used to build a `freshRecipeImages` key.
const freshRecipeImageOrder = ['CG', 'GS', 'RT', 'WS'];

/**
 * Resolves the fresh food pack shot from a variant's sachets.
 *
 * A recipe can occupy more than one sachet slot (two pouch sizes of the same
 * recipe, e.g. 8x 800g + 8x 600g GS), so types are de-duplicated before the key
 * is built. Returns `undefined` when the combination has no artwork (an unknown
 * recipe code, or no sachets at all) so callers can fall back to the Shopify
 * product image.
 */
export const getFreshRecipeImage = (
  sachets?: { type?: string; quantity?: number }[],
): string | undefined => {
  if (!sachets?.length) return undefined;

  const types = new Set(
    sachets
      .filter((sachet) => (sachet?.quantity ?? 0) > 0)
      .map((sachet) => String(sachet?.type ?? '').toUpperCase()),
  );

  const orderedTypes = freshRecipeImageOrder.filter((type) => types.has(type));

  // A recipe we have no artwork for is present, so don't show a wrong box.
  if (orderedTypes.length !== types.size) return undefined;

  return freshRecipeImages[orderedTypes.join('')];
};
